import { useMemo } from 'react';
import { DENSITY_RANGES, percentuale } from '@leopart/calc';
import { DistributionSlice } from '../../../shared/charts/chart.types';
import { ITrainingMixGameDensity } from '../../../models/allenamenti';
import { buildFlowBars } from './analysis.constants';

/** Riga di dettaglio per una banda di densità: dati + barre modalità pronte per il render. */
export interface IGameDensityRangeDetail {
  id: string;
  label: string;
  color: string;
  minutes: number;
  pct: number | null;
  flowBars: DistributionSlice[];
}

/**
 * Logica derivata del pannello «Densità di gioco»: mappa i dati già pronti
 * dal server (`data.gameDensity`) sulle costanti @leopart/calc (DENSITY_RANGES/
 * FLOWS, label e colori), senza ricalcolare nessuna soglia qui — stesso
 * pattern di `usePlayerGroupPanel` (F2).
 *
 * `rangeDetails` include SOLO le bande con minuti > 0: a differenza dei
 * gruppi dimensionali (sempre 4, anche vuoti), qui una banda senza minuti non
 * ha materia da mostrare nel breakdown per modalità.
 */
export const useGameDensityPanel = (density: ITrainingMixGameDensity | null) => {
  const totalMinutes = useMemo(
    () => density?.byRange.reduce((s, r) => s + r.minutes, 0) ?? 0,
    [density],
  );

  const rangeBars: DistributionSlice[] = useMemo(() => {
    if (!density) return [];
    return DENSITY_RANGES.map((range) => {
      const minutes = density.byRange.find((r) => r.key === range.id)?.minutes ?? 0;
      const bound = range.max == null ? `≥${range.min}` : `${range.min}–${range.max}`;
      return {
        id: range.id,
        label: `${range.labelIt} (${bound})`,
        value: minutes,
        percent: percentuale(minutes, totalMinutes),
        color: range.color,
      };
    });
  }, [density, totalMinutes]);

  const rangeDetails: IGameDensityRangeDetail[] = useMemo(() => {
    if (!density) return [];
    return DENSITY_RANGES.map((range) => {
      const row = density.byRange.find((r) => r.key === range.id);
      const minutes = row?.minutes ?? 0;
      return {
        id: range.id,
        label: range.labelIt,
        color: range.color,
        minutes,
        pct: percentuale(minutes, totalMinutes),
        // Senza fattore (×0.65): nella mini-card stretta l'etichetta andrebbe a capo.
        flowBars: buildFlowBars(row?.byFlow ?? [], false),
      };
    }).filter((r) => r.minutes > 0);
  }, [density, totalMinutes]);

  const totalFlowBars: DistributionSlice[] = useMemo(
    () => (density ? buildFlowBars(density.byFlowTotal) : []),
    [density],
  );

  return { rangeBars, rangeDetails, totalFlowBars };
};
